/**
 * RSPoolSection — RS Pool approvals, replacing the inline single-RS rendering
 * that used to live in CommissionBoard. Now reads `rs_pool_members` and renders
 * each deal's pool split across active members; trainees show as "Training"
 * lines with $0 and an Activate button (Jennifer-only).
 *
 * Activation rule (per Greg, May 1): first activation makes the trainee a
 * permanent member. Active members rebalance equally to make room for the new
 * member's % share.
 *
 * Oct 8, 2026 — approving goes through the `rs_pool_approve` RPC with the
 * pool lines (deal × member × share, lib/rsPoolLines — the same split
 * rs_pool_mark_paid takes): it flags rs_pool_approved and makes ONE Books
 * bill per roof-team member for the batch; Shannon pays it in Books.
 *
 * Spec: memory/features/finance-board-redesign.md (Ship 11, §10c #8, §10b #4-5)
 */

import { useEffect, useMemo, useState, useCallback } from "react";
import { supabase } from "../../lib/supabase";
import type { TeamRole } from "../../lib/types";
import { fmtUSD } from "../../lib/analytics";
import { calcScope } from "../../lib/scopeCalc";
import DealLink from "../DealLink";
import { useRealtimeTable } from "../../lib/useRealtimeTable";
import ActivateRSModal from "./ActivateRSModal";
import { formatSupabaseError } from "../../lib/formatSupabaseError";
// Type-only import — CommissionBoard imports this component, so a runtime
// import back would be circular. The `pending` prop is the full CommissionDeal
// row (COMMISSION_DEAL_SELECT), which carries every scopeCalc input.
import type { CommissionDeal } from "./CommissionBoard";
import { RS_POOL_RATE_LIVE as RS_POOL_RATE, rsPoolLinesFor } from "../../lib/rsPoolLines";

interface RSMember {
  id: string;
  team_member_id: string;
  status: "training" | "active" | "retired";
  share: number | null;       // 0..1
  training_since: string | null;
}

type PendingDeal = CommissionDeal;

function getDealRev(d: PendingDeal): number {
  // Must mirror CommissionBoard.getRSDealRev: the RS pool is 1.5% of the SAME
  // commissionable base as the RM commission. The old fallback here was the
  // legacy `acv + deductible` proxy, which ignored depreciation, PWI, upgrades,
  // credits, and supplement exclusion — under/over-stating the pool on any
  // non-trivial scope (e.g. Clay Boatman: $296.20 shown vs $423.80 correct).
  return Number(d.commissionable_revenue) || calcScope(d).commissionable;
}

export default function RSPoolSection({
  pending,
  members,
  role,
  isSuperUser,
  onApproved,
}: {
  pending: PendingDeal[];
  members: Record<string, { id: string; full_name: string }>;
  role: TeamRole | undefined;
  isSuperUser: boolean;
  onApproved: () => void;
}) {
  const [poolMembers, setPoolMembers] = useState<RSMember[] | null>(null);
  const [acting, setActing] = useState<Set<string>>(new Set());
  const [activatingMember, setActivatingMember] = useState<RSMember | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const reload = useCallback(() => setReloadKey((k) => k + 1), []);
  useRealtimeTable("rs_pool_members", reload);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      const { data, error } = await supabase
        .from("rs_pool_members")
        .select("*")
        .order("status", { ascending: true });
      if (cancelled) return;
      if (error) { setError(error.message); return; }
      setPoolMembers((data ?? []) as RSMember[]);
    }
    load();
    return () => { cancelled = true; };
  }, [reloadKey]);

  const canApprove = useMemo(
    () => isSuperUser || role === "qc" || role === "director_of_operations" || role === "roof_director" || role === "admin",
    [role, isSuperUser],
  );
  const canManagePool = canApprove;  // Jennifer + super-users manage RS pool membership

  if (!poolMembers) return <div className="p-4 text-slate-500 text-sm">Loading RS pool…</div>;

  const active = poolMembers.filter((m) => m.status === "active" && (m.share ?? 0) > 0);
  const trainees = poolMembers.filter((m) => m.status === "training");

  if (active.length === 0 && trainees.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow p-6 text-sm text-slate-500 text-center">
        No RS pool members yet. Add Maverick / Robert / Devon to <code>rs_pool_members</code> to seed.
      </div>
    );
  }

  // The pool lines rs_pool_approve stores: every active member's share of
  // every deal given (the RPC refuses a line whose deal is not in the batch).
  function poolLinesFor(ids: string[]) {
    return ids.flatMap((id) => {
      const d = pending.find((x) => x.id === id);
      return d ? rsPoolLinesFor(id, getDealRev(d), active) : [];
    });
  }

  async function approveRpc(ids: string[]) {
    const { error: rpcErr } = await supabase.rpc("rs_pool_approve", { p_deal_ids: ids, p_lines: poolLinesFor(ids) });
    if (rpcErr) throw rpcErr;
  }

  async function approvePool(dealId: string) {
    setActing((prev) => new Set(prev).add(dealId));
    setError(null);
    try {
      await approveRpc([dealId]);
      onApproved();
    } catch (err) {
      setError(formatSupabaseError(err));
    } finally {
      setActing((s) => { const n = new Set(s); n.delete(dealId); return n; });
    }
  }

  async function approveAll() {
    const ids = pending.map((d) => d.id);
    if (ids.length === 0) return;
    setActing(new Set(ids));
    setError(null);
    try {
      // One call = one approval batch = one bill per member for all of them.
      await approveRpc(ids);
      onApproved();
    } catch (err) {
      setError(formatSupabaseError(err));
    } finally {
      setActing(new Set());
    }
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div>
          <h2 className="text-sm font-semibold text-bardin-ink">RS Pool ({(RS_POOL_RATE * 100).toFixed(1)}%)</h2>
          <p className="text-xs text-slate-500">
            Jennifer approves after Chad confirms revenue. Pool splits across {active.length} active RS
            {trainees.length > 0 ? ` · ${trainees.length} in training` : ""}.
          </p>
        </div>
        {pending.length > 0 && canApprove && (
          <button
            onClick={approveAll}
            className="px-3 py-1.5 text-xs font-medium rounded bg-blue-600 text-white hover:bg-blue-700"
          >
            Approve All RS ({pending.length})
          </button>
        )}
      </div>

      {error && <div className="bg-red-50 text-red-700 text-xs p-2 rounded mb-2">⚠ {error}</div>}

      {pending.length === 0 ? (
        <div className="bg-white rounded-xl shadow p-6 text-sm text-slate-500 text-center">
          No RS pool approvals pending.
        </div>
      ) : (
        <div className="space-y-2">
          {/* Oct 6, 2026 — itemized by roof team member: what each person
              would be owed if every pending pool below is approved. */}
          <div className="bg-white rounded-xl shadow p-4">
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-2">
              Pending by roof team member · {pending.length} {pending.length === 1 ? "deal" : "deals"}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {active.map((m) => {
                const total = pending.reduce((sum, d) => sum + getDealRev(d) * RS_POOL_RATE * (m.share ?? 0), 0);
                return (
                  <div key={m.id} className="rounded-lg bg-blue-50 px-3 py-2">
                    <div className="text-xs text-blue-800 truncate">{members[m.team_member_id]?.full_name ?? "—"}</div>
                    <div className="text-base font-semibold text-bardin-ink">{fmtUSD(total)}</div>
                  </div>
                );
              })}
            </div>
          </div>
          {pending.map((d) => {
            const rev = getDealRev(d);
            const poolTotal = rev * RS_POOL_RATE;
            const ownerName = d.owner_id ? members[d.owner_id]?.full_name ?? "—" : "—";

            return (
              <div key={`rs-${d.id}`} className="bg-white rounded-xl shadow p-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-sm truncate">
                      <DealLink id={d.id}>{d.homeowner_name ?? "(no name)"}</DealLink>
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      {d.property_street ?? "—"}{d.claim_number ? ` · #${d.claim_number}` : ""}
                    </div>
                    <div className="text-xs text-slate-400 mt-0.5">RM: {ownerName}</div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className="text-sm font-semibold text-blue-700">{fmtUSD(poolTotal)}</div>
                    <div className="text-xs text-slate-500">pool total</div>
                  </div>
                </div>

                {/* Active member splits */}
                <div className="mt-3 border-t border-dashed border-slate-200 pt-3">
                  {active.map((m) => {
                    const memberName = members[m.team_member_id]?.full_name ?? "—";
                    const sharePct = (m.share ?? 0) * 100;
                    const amount = poolTotal * (m.share ?? 0);
                    return (
                      <div key={m.id} className="flex items-center justify-between py-1 text-sm">
                        <span className="text-slate-700">
                          <strong>{memberName}</strong>
                          <span className="text-slate-500 text-xs ml-1">RS · {sharePct.toFixed(0)}% of pool</span>
                        </span>
                        <span className="font-bold text-blue-700">{fmtUSD(amount)}</span>
                      </div>
                    );
                  })}

                  {/* Trainee lines — orange-tinted with Activate button */}
                  {trainees.map((m) => {
                    const memberName = members[m.team_member_id]?.full_name ?? "—";
                    return (
                      <div
                        key={m.id}
                        className="flex items-center justify-between py-1.5 px-2 -mx-2 my-1 rounded text-sm bg-orange-50/60"
                      >
                        <span className="text-slate-700">
                          <strong>{memberName}</strong>
                          <span className="ml-2 text-[10px] font-bold px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">
                            🎓 Training{m.training_since ? ` since ${m.training_since}` : ""}
                          </span>
                        </span>
                        <span className="flex items-center gap-2">
                          <span className="font-semibold text-slate-400">$0.00</span>
                          {canManagePool && (
                            <button
                              onClick={() => setActivatingMember(m)}
                              className="px-2 py-0.5 text-[11px] font-medium rounded bg-bardin-orange text-white hover:opacity-90"
                            >
                              ✓ Activate
                            </button>
                          )}
                        </span>
                      </div>
                    );
                  })}
                </div>

                {canApprove && (
                  <div className="mt-3">
                    <button
                      onClick={() => approvePool(d.id)}
                      disabled={acting.has(d.id)}
                      className="px-4 py-1.5 text-xs font-medium rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50"
                    >
                      {acting.has(d.id) ? "Approving…" : `Approve RS Pool — ${active.length} split${active.length === 1 ? "" : "s"}`}
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {activatingMember && (
        <ActivateRSModal
          trainee={activatingMember}
          activeMembers={active}
          memberMap={members}
          onClose={() => setActivatingMember(null)}
          onActivated={() => { setActivatingMember(null); reload(); }}
        />
      )}
    </div>
  );
}
